import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PlusIcon, PowerIcon, ZapIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router';

import { WorkflowScopeType, WorkflowStatus } from '@invenco/common-domain/enums';
import { WorkflowDTO } from '@invenco/common-interface/automation';
import { WmsInstanceDTO } from '@invenco/warehousing-interface';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  useRowNavigationCallback,
  type View,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { workflowStatusType } from '@/shared/status-maps';
import {
  workflowStatusTitle,
  workflowDataTypeTitle,
  workflowTriggerTypeTitle,
} from '@/shared/title-maps';

type WorkflowListItem = WorkflowDTO & {
  wmsInstanceCode?: string;
};

const columnHelper = createColumnHelper<WorkflowListItem>();

const accountColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    meta: { truncate: true },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge status={getValue()} type={workflowStatusType} title={workflowStatusTitle} />
    ),
  }),
  columnHelper.accessor('dataType', {
    header: 'Data type',
    cell: ({ getValue }) => <Badge variant="secondary">{workflowDataTypeTitle[getValue()]}</Badge>,
  }),
  columnHelper.accessor('triggerType', {
    header: 'Trigger',
    cell: ({ getValue }) => (
      <Badge variant="secondary">{workflowTriggerTypeTitle[getValue()]}</Badge>
    ),
  }),
];

const wmsColumns = [
  columnHelper.accessor('scopeId', {
    id: 'wmsInstance',
    header: 'WMS Instance',
    cell: ({ row }) => {
      const instanceId = row.original.scopeId;
      return row.original.wmsInstanceCode ?? instanceId;
    },
  }),
  ...accountColumns,
];

type Props = {
  scopeType: WorkflowScopeType;
};

export const presetViews: View[] = [
  { id: 'all', name: 'All' },
  {
    id: WorkflowStatus.ACTIVE,
    name: workflowStatusTitle[WorkflowStatus.ACTIVE],
    state: {
      filters: [{ id: 'status', operator: 'eq', value: [WorkflowStatus.ACTIVE] }],
    },
  },
  {
    id: WorkflowStatus.INACTIVE,
    name: workflowStatusTitle[WorkflowStatus.INACTIVE],
    state: {
      filters: [{ id: 'status', operator: 'eq', value: [WorkflowStatus.INACTIVE] }],
    },
  },
];

const mapWmsInstanceCodesById = (data: WmsInstanceDTO[] = []) =>
  Object.fromEntries(data.map(({ id, code }) => [id, code])) as Record<
    WmsInstanceDTO['id'],
    WmsInstanceDTO['code']
  >;

export function WorkflowList({ scopeType }: Props) {
  const selectWorkflowsInScope = useCallback(
    (data: WorkflowDTO[] = []) => data.filter((workflow) => workflow.scopeType === scopeType),
    [scopeType],
  );

  const { automationGateway, warehousingGateway } = useGateways();

  const columns = useMemo(
    () => (scopeType === WorkflowScopeType.WMS_INSTANCE ? wmsColumns : accountColumns),
    [scopeType],
  );

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'workflows',
    presetViews,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      status: {
        variant: 'select',
        label: 'Status',
        icon: PowerIcon,
        options: [WorkflowStatus.ACTIVE, WorkflowStatus.INACTIVE],
        optionLabel: (option) => workflowStatusTitle[option as WorkflowStatus] || option,
      },
    },
    views,
    isLoadingSavedViews,
  });

  const activeFilters = filters.getActiveFilterMap();

  /**
   * TODO - support operators in the BE
   *
   * Assumes exactly two possible statuses (ACTIVE, INACTIVE) and two operators (eq, ne).
   * - 'eq' returns the selected status
   * - 'ne' returns the opposite status (since the API only supports filtering by a single status)
   */
  const statusFilter = useMemo(() => {
    const activeStatusFilter = activeFilters?.status;
    if (!activeStatusFilter) return undefined;
    const status = activeStatusFilter.value[0] as WorkflowStatus;

    if (activeStatusFilter.operator === 'eq') return status;
    if (activeStatusFilter.operator === 'ne') {
      return status === WorkflowStatus.ACTIVE ? WorkflowStatus.INACTIVE : WorkflowStatus.ACTIVE;
    }

    return undefined;
  }, [activeFilters?.status]);

  const {
    data: scopedWorkflows,
    isLoading: isLoadingWorkflows,
    isFetching: isFetchingWorkflows,
    isPlaceholderData,
    isError,
  } = useQueryWithInput({
    parentKey: 'workflows',
    input: {
      search: filters.search,
      status: statusFilter,
    },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        automationGateway.getWorkflows(
          { search: input.search, status: input.status, cursor, take },
          { signal },
        ),
      ),
    select: selectWorkflowsInScope,
    placeholderData: keepPreviousData,
  });

  const { data: wmsInstanceCodesById, isLoading: isLoadingWmsInstances } = useQuery({
    queryKey: ['wmsInstances'],
    queryFn: ({ signal }) => warehousingGateway.getWmsInstances(undefined, { signal }),
    enabled: scopeType === WorkflowScopeType.WMS_INSTANCE,
    select: mapWmsInstanceCodesById,
  });

  const workflowListItems = useMemo<WorkflowListItem[]>(() => {
    if (!scopedWorkflows) {
      return [];
    }

    if (scopeType === WorkflowScopeType.WMS_INSTANCE) {
      const codes = wmsInstanceCodesById ?? {};
      return scopedWorkflows.map((workflow) => ({
        ...workflow,
        wmsInstanceCode: codes[workflow.scopeId],
      }));
    }

    return scopedWorkflows;
  }, [scopedWorkflows, wmsInstanceCodesById, scopeType]);

  const onClickRow = useRowNavigationCallback<WorkflowListItem>('/automation/workflows');

  const table = useDataTable({
    data: workflowListItems,
    columns,
    isLoading: isLoadingWorkflows || isLoadingWmsInstances,
    isLoadingWithStaleData: isFetchingWorkflows && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ZapIcon />
        </HeaderIcon>
        <HeaderTitle>Workflows</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="/automation/workflows/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={workflowListItems.length}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
